import React from 'react';
import { Flame, Zap } from 'lucide-react';

interface UsageChartProps {
  gamesRemaining: number;
  gamesTotal: number;
  predictionsRemaining: number;
  predictionsTotal: number;
  isDarkMode: boolean;
  resetTime: number | null;
  bonusGames?: number;
  bonusPredictions?: number;
}

export const UsageChart: React.FC<UsageChartProps> = ({
  gamesRemaining,
  gamesTotal,
  predictionsRemaining,
  predictionsTotal,
  isDarkMode,
  resetTime,
  bonusGames = 0,
  bonusPredictions = 0,
}) => {
  const isMax = predictionsTotal > 100000; // Max users have effectively unlimited predictions
  const isPro = gamesTotal > 10000 && !isMax; // Pro users have unlimited games but limited predictions
  const hasSubscription = isMax || isPro;
  
  const gamesPercentage = (isMax || isPro) ? 100 : (gamesRemaining / gamesTotal) * 100;
  const predictionsPercentage = isMax ? 100 : (predictionsRemaining / predictionsTotal) * 100;

  const getTimeUntilReset = () => {
    if (!resetTime) return '';
    const hours = Math.ceil((resetTime - Date.now()) / (1000 * 60 * 60));
    return `Resets in ${hours}h`;
  };

  const getBarColor = (percentage: number) => {
    if (percentage > 50) return isDarkMode ? 'bg-green-500' : 'bg-green-600';
    if (percentage > 25) return isDarkMode ? 'bg-yellow-500' : 'bg-yellow-600';
    return isDarkMode ? 'bg-red-500' : 'bg-red-600';
  };

  return (
    <div className={`p-4 rounded-xl border ${
      isMax
        ? 'bg-gradient-to-r from-purple-500/10 to-pink-500/10 border-purple-500/50'
        : isPro 
        ? 'bg-gradient-to-r from-blue-500/10 to-purple-500/10 border-blue-500/50'
        : isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
    }`}>
      <div className="flex items-center justify-between mb-3">
        <h3 className={`text-sm font-semibold ${
          isMax
            ? 'bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent'
            : isPro 
            ? 'bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent'
            : isDarkMode ? 'text-gray-200' : 'text-gray-800'
        }`}>
          Daily Usage {hasSubscription && '✨'}
        </h3>
        {resetTime && (
          <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            {getTimeUntilReset()}
          </span>
        )}
      </div>

      {/* Games Usage */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Flame size={14} className={isDarkMode ? 'text-orange-400' : 'text-orange-600'} />
            <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Games
            </span>
          </div>
          <div className="text-right">
            <span className={`text-xs font-bold ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
              {(isMax || isPro) ? '∞ Unlimited' : `${gamesRemaining}/${gamesTotal}`}
            </span>
            {bonusGames > 0 && !isMax && !isPro && (
              <span className={`block text-[10px] ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                +{bonusGames} bonus
              </span>
            )}
          </div>
        </div>
        <div className={`w-full h-2 rounded-full overflow-hidden ${
          isDarkMode ? 'bg-gray-700' : 'bg-gray-200'
        }`}>
          <div
            className={`h-full transition-all duration-300 ${
              isMax 
                ? 'bg-gradient-to-r from-purple-500 to-pink-500'
                : isPro 
                ? 'bg-gradient-to-r from-blue-500 to-purple-500' 
                : getBarColor(gamesPercentage)
            }`}
            style={{ width: `${gamesPercentage}%` }}
          />
        </div>
      </div>

      {/* Predictions Usage */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Zap size={14} className={isDarkMode ? 'text-blue-400' : 'text-blue-600'} />
            <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Predictions
            </span>
          </div>
          <div className="text-right">
            <span className={`text-xs font-bold ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
              {isMax ? '∞ Unlimited' : `${predictionsRemaining}/${predictionsTotal}`}
            </span>
            {bonusPredictions > 0 && !isMax && (
              <span className={`block text-[10px] ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                +{bonusPredictions} bonus
              </span>
            )}
          </div>
        </div>
        <div className={`w-full h-2 rounded-full overflow-hidden ${
          isDarkMode ? 'bg-gray-700' : 'bg-gray-200'
        }`}>
          <div
            className={`h-full transition-all duration-300 ${
              isMax 
                ? 'bg-gradient-to-r from-purple-500 to-pink-500'
                : getBarColor(predictionsPercentage)
            }`}
            style={{ width: `${predictionsPercentage}%` }}
          />
        </div>
      </div>
    </div>
  );
};
